import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    usePlandokumenterForPlan,
    useCreatePlandokument,
    useDeletePlandokument,
    useFetchPlandokumentAttachmentUrl,
    plandokumentInputSchema,
} from '@origo/planregister';
import { Loader, Notice } from '@origo/origo-ui';
import { useToast } from '@origo/origo-components';
import DocumentsPage, { type NewDocumentState, type ReplaceDocumentState } from './DocumentsPage';
import type { DocumentFieldErrors } from './components/DocumentForm';
import {
    emptyDocumentForm,
    toDocumentFormValues,
    toDocumentItem,
    toPlandokumentInput,
} from '../../mappers/toDocuments';
import type { DocumentFormValues } from '../../types';

interface DocumentsContainerProps {
    napId: string;
}

/** Feltnære feil fra pakkens zod-schema (jf. ProcessingsContainer). */
function collectFieldErrors(input: unknown): DocumentFieldErrors | null {
    const parsed = plandokumentInputSchema.safeParse(input);
    if (parsed.success) return null;
    const next: DocumentFieldErrors = {};
    for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof DocumentFormValues | undefined;
        if (key && !next[key]) next[key] = issue.message;
    }
    return next;
}

const DocumentsContainer: React.FC<DocumentsContainerProps> = ({ napId }) => {
    const { t } = useTranslation();
    const toast = useToast();
    const { data, isLoading, isError } = usePlandokumenterForPlan(napId);
    const create = useCreatePlandokument();
    const remove = useDeletePlandokument();
    const fetchAttachmentUrl = useFetchPlandokumentAttachmentUrl();

    // Nyeste øverst — sorterer på dokumentdato (desc). Dokumenter uten
    // dato havner sist, sortert stabilt etter opprinnelig array-orden.
    const items = useMemo(() => {
        const sorted = [...(data ?? [])].sort((a, b) => {
            const aTime = a.dokumentdato?.getTime() ?? -Infinity;
            const bTime = b.dokumentdato?.getTime() ?? -Infinity;
            return bTime - aTime;
        });
        return sorted.map(doc => toDocumentItem(doc, t));
    }, [data, t]);

    // --- nytt dokument (F3.1) ---
    const [newOpen, setNewOpen] = useState(false);
    const [newDraft, setNewDraft] = useState<DocumentFormValues>(() => emptyDocumentForm());
    const [newFile, setNewFile] = useState<File | null>(null);
    const [newErrors, setNewErrors] = useState<DocumentFieldErrors>({});
    const [fileError, setFileError] = useState<string | undefined>(undefined);

    const openNew = useCallback(() => {
        setNewDraft(emptyDocumentForm());
        setNewFile(null);
        setNewErrors({});
        setFileError(undefined);
        setNewOpen(true);
    }, []);

    const changeNew = useCallback((field: keyof DocumentFormValues, value: string) => {
        setNewDraft(d => ({ ...d, [field]: value }));
        setNewErrors(e => (e[field] ? { ...e, [field]: undefined } : e));
    }, []);

    const changeNewFile = useCallback((file: File | null) => {
        setNewFile(file);
        setFileError(undefined);
    }, []);

    const saveNew = useCallback(async () => {
        const input = toPlandokumentInput(newDraft, napId);
        const errors = collectFieldErrors(input);
        const missingFile = !newFile;
        setNewErrors(errors ?? {});
        setFileError(missingFile ? t('documents.new.fileRequired') : undefined);
        if (errors || missingFile) return;

        const created = await toast.execute(() => create.mutateAsync({ input, file: newFile ?? undefined }), {
            success: t('documents.uploaded'),
        });
        if (created) {
            setNewOpen(false);
            setNewDraft(emptyDocumentForm());
            setNewFile(null);
        }
    }, [newDraft, newFile, napId, toast, create, t]);

    const [replaceId, setReplaceId] = useState<string | null>(null);
    const [replaceDraft, setReplaceDraft] = useState<DocumentFormValues>(() => emptyDocumentForm());
    const [replaceFile, setReplaceFile] = useState<File | null>(null);
    const [replaceErrors, setReplaceErrors] = useState<DocumentFieldErrors>({});
    const [replaceFileError, setReplaceFileError] = useState<string | undefined>(undefined);

    const replaceOriginal = useMemo(
        () => items.find(i => i.id === replaceId) ?? null,
        [items, replaceId]
    );

    const startReplace = useCallback(
        (id: string) => {
            const original = data?.find(d => String(d.objectId) === id);
            if (!original) return;
            setReplaceDraft(toDocumentFormValues(original));
            setReplaceFile(null);
            setReplaceErrors({});
            setReplaceFileError(undefined);
            setReplaceId(id);
        },
        [data]
    );

    const cancelReplace = useCallback(() => {
        setReplaceId(null);
        setReplaceDraft(emptyDocumentForm());
        setReplaceFile(null);
        setReplaceErrors({});
        setReplaceFileError(undefined);
    }, []);

    const changeReplace = useCallback((field: keyof DocumentFormValues, value: string) => {
        setReplaceDraft(d => ({ ...d, [field]: value }));
        setReplaceErrors(e => (e[field] ? { ...e, [field]: undefined } : e));
    }, []);

    const changeReplaceFile = useCallback((file: File | null) => {
        setReplaceFile(file);
        setReplaceFileError(undefined);
    }, []);

    const saveReplace = useCallback(async () => {
        const original = data?.find(d => String(d.objectId) === replaceId);
        if (!original) return;
        const input = toPlandokumentInput(replaceDraft, napId);
        const errors = collectFieldErrors(input);
        const missingFile = !replaceFile;
        setReplaceErrors(errors ?? {});
        setReplaceFileError(missingFile ? t('documents.new.fileRequired') : undefined);
        if (errors || missingFile) return;

        try {
            await create.mutateAsync({ input, file: replaceFile ?? undefined });
        } catch {
            toast.error(t('documents.replace.createFailed'));
            return;
        }
        try {
            await remove.mutateAsync(original.objectId);
            toast.success(t('documents.replaced'));
        } catch {
            toast.warning(t('documents.replace.cleanupNeeded', { navn: original.tittel }));
        }
        cancelReplace();
    }, [data, replaceId, replaceDraft, replaceFile, napId, toast, create, remove, t, cancelReplace]);

    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const pendingDelete = useMemo(
        () => items.find(i => i.id === pendingDeleteId) ?? null,
        [items, pendingDeleteId]
    );

    const confirmDelete = useCallback(async () => {
        const doc = data?.find(d => String(d.objectId) === pendingDeleteId);
        if (!doc) return;
        const ok = await toast.execute(
            async () => {
                await remove.mutateAsync(doc.objectId);
                return true;
            },
            { success: t('documents.deleted') }
        );
        if (ok) setPendingDeleteId(null);
    }, [data, pendingDeleteId, toast, remove, t]);

    if (isLoading) return <Loader label={t('common.loading')} />;
    if (isError) return <Notice variant="danger">{t('common.errorLoading')}</Notice>;

    const newDocument: NewDocumentState = {
        open: newOpen,
        values: newDraft,
        file: newFile,
        fieldErrors: newErrors,
        fileError,
        isSaving: create.isPending,
    };

    const replaceDocument: ReplaceDocumentState = {
        open: replaceId !== null,
        original: replaceOriginal,
        values: replaceDraft,
        file: replaceFile,
        fieldErrors: replaceErrors,
        fileError: replaceFileError,
        isSaving: create.isPending || remove.isPending,
    };

    return (
        <DocumentsPage
            items={items}
            onOpen={async id => {
                const doc = data?.find(d => String(d.objectId) === id);
                if (!doc?.referansedokumentfil) return;
                const blobUrl = await fetchAttachmentUrl(doc.referansedokumentfil);
                if (blobUrl) window.open(blobUrl, '_blank', 'noopener,noreferrer');
            }}
            newDocument={newDocument}
            onNewOpen={openNew}
            onNewClose={() => setNewOpen(false)}
            onNewChange={changeNew}
            onNewFileChange={changeNewFile}
            onNewSave={saveNew}
            replaceDocument={replaceDocument}
            onReplaceRequest={startReplace}
            onReplaceChange={changeReplace}
            onReplaceFileChange={changeReplaceFile}
            onReplaceSave={saveReplace}
            onReplaceCancel={cancelReplace}
            onDeleteRequest={setPendingDeleteId}
            pendingDelete={pendingDelete}
            onDeleteConfirm={confirmDelete}
            onDeleteCancel={() => setPendingDeleteId(null)}
            isDeleting={remove.isPending}
        />
    );
};

export default DocumentsContainer;
